import { SALESFORCE_FIELD_MAX_LENGTH, exceedsFieldLength } from '../../constants';
import { andList } from '../../excelImport/sheetCells';

import { parseIsoDateUtc } from './isoDates';
import type {
  CompleteSurchargePeriod,
  SurchargePeriod,
  SurchargePeriodFormValues,
  SurchargePeriodSetting,
} from './types';

const SETTING_LABELS: Record<SurchargePeriodSetting, string> = {
  rooms: 'Rooms',
  options: 'Options',
  collectedBy: 'Collected by',
  accumulates: 'Accumulate',
  overlapping: 'Overlapping',
};

const SETTINGS: ReadonlyArray<SurchargePeriodSetting> = [
  'rooms',
  'options',
  'collectedBy',
  'accumulates',
  'overlapping',
];

/** Labels of the settings still to pick; an empty room or option list counts as unset. */
export function unsetSurchargeFields(values: SurchargePeriodFormValues): Array<string> {
  return SETTINGS.filter((setting) => {
    const value = values[setting];
    return value == null || (Array.isArray(value) && value.length === 0);
  }).map((setting) => SETTING_LABELS[setting]);
}

// A plain boolean, not a type guard: negating a guard inside find/filter narrows the period to never.
export function hasUnsetSurchargeFields(values: SurchargePeriodFormValues): boolean {
  return unsetSurchargeFields(values).length > 0;
}

export function isCompleteSurchargePeriod(period: SurchargePeriod): period is CompleteSurchargePeriod {
  return !hasUnsetSurchargeFields(period);
}

export function describeIncompleteSurchargePeriod(periods: ReadonlyArray<SurchargePeriod>): string | undefined {
  const incomplete = periods.find(hasUnsetSurchargeFields);
  return incomplete && `Surcharge period "${incomplete.name}" is missing ${andList(unsetSurchargeFields(incomplete))}`;
}

export const SURCHARGE_NAME_TOO_LONG = `Period name must be ${SALESFORCE_FIELD_MAX_LENGTH.surchargeName} characters or fewer.`;

/** `specificDays` is form-only: a period saved with no weekdays already means every day. */
export function validateSurchargePeriod(values: SurchargePeriodFormValues, specificDays = false): Array<string> {
  const errors: Array<string> = [];

  if (values.name.trim().length < 1) {
    errors.push('Period name is required.');
  }

  if (exceedsFieldLength(values.name, SALESFORCE_FIELD_MAX_LENGTH.surchargeName)) {
    errors.push(SURCHARGE_NAME_TOO_LONG);
  }

  const start = parseIsoDateUtc(values.startDate);
  const end = parseIsoDateUtc(values.endDate);

  if (start == null) {
    errors.push('From must be a real ISO YYYY-MM-DD date.');
  }

  if (end == null) {
    errors.push('To must be a real ISO YYYY-MM-DD date.');
  }

  if (start != null && end != null && values.startDate > values.endDate) {
    errors.push('From must be on or before To.');
  }

  if (!Number.isFinite(values.sellAud) || values.sellAud <= 0) {
    errors.push('Sell AUD must be greater than 0.');
  }

  if (values.cost != null && (!Number.isFinite(values.cost) || values.cost < 0)) {
    errors.push('Cost must be 0 or more.');
  }

  if (specificDays && values.weekdays.length === 0) {
    errors.push('Pick at least one day of the week.');
  }

  const unset = unsetSurchargeFields(values);
  if (unset.length > 0) {
    errors.push(`Set ${andList(unset)}.`);
  }

  return errors;
}
